import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { deleteFundraiser } from '../../services/campaign';

/**
 * Copying a fundraiser saves the duplicate server-side before the user ever
 * sees the edit form, so plain navigation away leaves a stray row behind.
 * The copy flow flags the redirect with `?copied=1`; while that flag is live
 * Cancel means "discard this copy", not "go back".
 *
 * The flag is dropped the moment the user saves — from then on the copy is a
 * fundraiser like any other and Cancel goes back to being navigation.
 */
export const useDiscardableCopy = (fundraiserId?: number | string) => {
    const history = useHistory();
    const location = useLocation();
    const [isCopy, setIsCopy] = useState(
        new URLSearchParams(location.search).get('copied') === '1'
    );
    // Name of the fundraiser this one was copied from, for the banner that
    // tells the user the copy actually happened.
    const [copiedFrom] = useState(
        () => new URLSearchParams(location.search).get('from') || ''
    );
    const [confirmingDiscard, setConfirmingDiscard] = useState(false);
    const [discarding, setDiscarding] = useState(false);

    // Drop the flag from the address bar once it has been read into state.
    // Left in place, a reload after saving would re-arm the discard prompt on
    // a fundraiser the user has since edited.
    useEffect(() => {
        if (isCopy) {
            history.replace(location.pathname);
        }
        // Intentionally mount-only: the flag is only ever set by the redirect
        // out of the copy modal.
    }, []);

    const clearCopyFlag = () => setIsCopy(false);

    /**
     * Returns true when Cancel was intercepted, so callers can fall through to
     * their normal navigation when this is not an untouched copy.
     */
    const requestCancel = () => {
        if (!isCopy || !fundraiserId) return false;
        setConfirmingDiscard(true);
        return true;
    };

    const confirmDiscard = async () => {
        if (!fundraiserId) return;
        try {
            setDiscarding(true);
            const resp = await deleteFundraiser(Number(fundraiserId));
            if (resp.status !== 204) {
                throw new Error('Delete failed');
            }
            setConfirmingDiscard(false);
            setIsCopy(false);
            history.replace('/fundraisers');
        } catch (e) {
            toast.error('Problem occurred while discarding the copy!');
        } finally {
            setDiscarding(false);
        }
    };

    return {
        isCopy,
        copiedFrom,
        clearCopyFlag,
        requestCancel,
        confirmDiscard,
        confirmingDiscard,
        closeDiscardConfirm: () => setConfirmingDiscard(false),
        discarding,
    };
};
